import { useEffect, useState, type ReactNode } from 'react'
import { Alert, Button, Card, Form, Input, Spin, Tabs, Typography, message } from 'antd'
import { RobotOutlined } from '@ant-design/icons'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, setAdminToken } from '../api'
import { validateSession } from '../lib/sessionAuth'
import './Login.css'

const { Title, Text } = Typography

type LoginOptions = {
  password_enabled: boolean
  sms_enabled: boolean
}

export default function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectTo = searchParams.get('redirect') || '/'
  const [loading, setLoading] = useState(false)
  const [checkingSession, setCheckingSession] = useState(true)
  const [backendOk, setBackendOk] = useState<boolean | null>(null)
  const [options, setOptions] = useState<LoginOptions>({ password_enabled: true, sms_enabled: false })
  const [smsSending, setSmsSending] = useState(false)
  const [smsCountdown, setSmsCountdown] = useState(0)
  const [pwdForm] = Form.useForm()
  const [smsForm] = Form.useForm()
  useEffect(() => {
    api
      .get('/health')
      .then(() => setBackendOk(true))
      .catch(() => setBackendOk(false))
    api
      .get<LoginOptions>('/auth/login-options')
      .then((r) => setOptions(r.data))
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (smsCountdown <= 0) return
    const timer = window.setTimeout(() => setSmsCountdown((v) => v - 1), 1000)
    return () => window.clearTimeout(timer)
  }, [smsCountdown])

  useEffect(() => {
    let cancelled = false
    void validateSession()
      .then((ok) => {
        if (cancelled || !ok) return
        void navigate(redirectTo.startsWith('/') ? redirectTo : '/', { replace: true })
      })
      .finally(() => {
        if (!cancelled) setCheckingSession(false)
      })
    return () => {
      cancelled = true
    }
  }, [navigate, redirectTo])

  const finishLogin = (token: string, displayName: string) => {
    setAdminToken(token, displayName)
    message.success('登录成功')
    void navigate(redirectTo.startsWith('/') ? redirectTo : '/', { replace: true })
  }

  const onPasswordLogin = (v: { username: string; password: string }) => {
    setLoading(true)
    api
      .post<{ access_token: string; user: { display_name?: string; username: string } }>(
        '/auth/login',
        v,
      )
      .then((r) => finishLogin(r.data.access_token, r.data.user.display_name || r.data.user.username))
      .catch((e) => message.error(e.message))
      .finally(() => setLoading(false))
  }

  const sendSmsCode = () => {
    void smsForm.validateFields(['mobile']).then(({ mobile }) => {
      setSmsSending(true)
      api
        .post('/auth/sms/send', { mobile })
        .then(() => {
          message.success('验证码已发送')
          setSmsCountdown(60)
        })
        .catch((e) => message.error(e.message))
        .finally(() => setSmsSending(false))
    })
  }

  const onSmsLogin = (v: { mobile: string; code: string }) => {
    setLoading(true)
    api
      .post<{ access_token: string; user: { display_name?: string; username: string } }>(
        '/auth/sms/login',
        v,
      )
      .then((r) => finishLogin(r.data.access_token, r.data.user.display_name || r.data.user.username))
      .catch((e) => message.error(e.message))
      .finally(() => setLoading(false))
  }

  const pageShell = (content: ReactNode) => (
    <div className="login-page">
      <div className="login-page-bg" aria-hidden>
        <div className="login-page-bg-grid" />
        <div className="login-page-orb login-page-orb--1" />
        <div className="login-page-orb login-page-orb--2" />
        <div className="login-page-orb login-page-orb--3" />
      </div>
      {content}
    </div>
  )

  if (checkingSession) {
    return pageShell(
      <div className="login-page--loading">
        <Spin size="large" tip="正在校验登录状态…" />
      </div>,
    )
  }

  const tabItems = [
    {
      key: 'password',
      label: '账号密码',
      children: (
        <Form
          form={pwdForm}
          layout="vertical"
          onFinish={onPasswordLogin}
        >
          <Form.Item name="username" label="用户名" rules={[{ required: true, message: '请输入用户名' }]}>
            <Input placeholder="用户名 / 手机号" />
          </Form.Item>
          <Form.Item name="password" label="密码" rules={[{ required: true, message: '请输入密码' }]}>
            <Input.Password placeholder="登录密码" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={loading}>
            登录
          </Button>
        </Form>
      ),
    },
  ]

  if (options.sms_enabled) {
    tabItems.push({
      key: 'sms',
      label: '手机验证码',
      children: (
        <Form form={smsForm} layout="vertical" onFinish={onSmsLogin}>
          <Form.Item
            name="mobile"
            label="手机号"
            rules={[
              { required: true, message: '请输入手机号' },
              { pattern: /^1\d{10}$/, message: '请输入 11 位手机号' },
            ]}
          >
            <Input placeholder="已绑定账号的手机号" maxLength={11} />
          </Form.Item>
          <Form.Item label="验证码" required>
            <div className="login-sms-row">
              <Form.Item name="code" noStyle rules={[{ required: true, message: '请输入验证码' }]}>
                <Input placeholder="6 位验证码" maxLength={6} />
              </Form.Item>
              <Button disabled={smsCountdown > 0} loading={smsSending} onClick={sendSmsCode}>
                {smsCountdown > 0 ? `${smsCountdown}s` : '获取验证码'}
              </Button>
            </div>
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={loading}>
            登录
          </Button>
        </Form>
      ),
    })
  }

  return pageShell(
    <div className="login-page-inner">
      <aside className="login-hero">
        <div className="login-hero-badge">
          <RobotOutlined />
          <span>Enterprise AI Knowledge Base</span>
        </div>
        <Title level={2} className="login-hero-title">
          企业 AI 知识库
        </Title>
        <p className="login-hero-desc">
          统一接入企业文档与知识资产，支持智能检索、AI 对话、图谱分析与知识加工全流程。
        </p>
        <div className="login-hero-features">
          {['混合检索与 RAG 问答', '知识图谱与文档加工', '权限管控与组织协同'].map((text) => (
            <div key={text} className="login-hero-feature">
              <span className="login-hero-feature-dot" aria-hidden />
              {text}
            </div>
          ))}
        </div>
      </aside>
      <Card className="login-card" bordered={false}>
        <div className="login-brand">
          <span className="login-brand-icon">
            <RobotOutlined />
          </span>
          <Title level={4} style={{ margin: 0 }}>
            欢迎登录
          </Title>
        </div>
        <Text type="secondary">登录后使用知识检索、对话与管理功能</Text>
        {backendOk === false && (
          <Alert
            style={{ marginTop: 12 }}
            type="error"
            showIcon
            message="未检测到后端 API"
            description="请确认后端服务已启动（默认 127.0.0.1:18080）"
          />
        )}
        <Tabs style={{ marginTop: 16 }} items={tabItems} />
      </Card>
    </div>,
  )
}
